import { useExecuteQSetGeneratorMutation } from '@exam-management/graphql';
import { useNavigate, useParams } from '@exam-management/router';
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  type DialogProps,
  Form,
  Divider,
  DialogContent,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { GenerateQSetLoadingModal } from '../../components';
import { type GenerateQSetErrorCode } from '../../types';
import { GenerateQSetErrorAlert } from '../generate-qset-error-alert';
import { GenerateQSetForm } from '../generate-qset-form';

import { GenerateQSetFormSchema, type GenerateQSetFormInput } from './constants';
import { getErrorCode } from './utils';

interface GenerateQSetModalProps extends DialogProps {
  onClose: () => void;
}

export default function GenerateQSetModal(props: Readonly<GenerateQSetModalProps>) {
  const { onClose, open: isOpen, ...restProps } = props;
  const { courseId } = useParams('/exam-courses/:courseId/qsets');
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [error, setError] = useState<GenerateQSetErrorCode | null>(null);

  const formContext = useForm<GenerateQSetFormInput>({
    defaultValues: {
      qConfig: { value: '', label: '' },
    },
    resolver: zodResolver(GenerateQSetFormSchema),
  });
  const { handleSubmit, reset } = formContext;

  const handleClose = () => {
    setError(null);
    reset();
    onClose();
  };

  const [executeQSetGenerator, { loading: isExecuteQSetGeneratorLoading }] =
    useExecuteQSetGeneratorMutation({
      onCompleted: (data) => {
        navigate('/exam-courses/:courseId/qsets/generator/results', {
          params: {
            courseId,
          },
          state: {
            qSetGeneratorId: data.excExecuteQSetGenerator.qSetGeneratorExecution.id,
          },
        });
        reset();
      },

      onError: (error) => {
        const errorCode = getErrorCode(error);

        if (!errorCode) {
          showSnackbar({
            message: 'Unable to Generate QSets',
            variant: 'error',
          });
          reset();
          handleClose();
          return;
        }

        setError(errorCode);
      },
    });

  const onGenerateSubmit = async (formValues: GenerateQSetFormInput) => {
    setError(null);
    await executeQSetGenerator({
      variables: {
        executeQSetGeneratorInput: {
          qConfigId: formValues.qConfig.value,
          duplicationRate: formValues.duplicationRate,
          needTotal: formValues.needTotal,
        },
      },
    });
  };

  useEffect(() => {
    const subscription = formContext.watch((value, { name }) => {
      if (name === 'qConfig' && error) {
        setError(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [error, formContext.watch]);

  if (isExecuteQSetGeneratorLoading) {
    return <GenerateQSetLoadingModal />;
  }

  return (
    <Dialog
      open={isOpen}
      maxWidth='sm'
      slotProps={{
        paper: {
          component: Form,
          onSubmit: handleSubmit(onGenerateSubmit),
        },
      }}
      {...restProps}
    >
      <FormProvider {...formContext}>
        <DialogTitle onClose={handleClose}>Generate QSet</DialogTitle>
        <Divider />
        <DialogContent
          sx={(theme) => ({ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) })}
        >
          {error ? (
            <GenerateQSetErrorAlert
              examCourseId={courseId}
              errorCode={error}
              qConfigId={formContext.getValues('qConfig').value}
            />
          ) : null}
          <GenerateQSetForm />
          <Text variant='caption' color='text.secondary'>
            ระยะเวลาในการสร้างชุดข้อสอบขึ้นอยู่กับจำนวนชุดข้อสอบที่ต้องการ
          </Text>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button variant='outlined' onClick={handleClose} color='secondary' size='large'>
            Cancel
          </Button>
          <Button type='submit' color='primary' size='large'>
            Generate QSet
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
